import * as THREE from 'three';
import { Engine } from './core/Engine.js';
import { Vehicle } from './gameplay/Vehicle.js';
import { RailPath } from './gameplay/RailPath.js';
import { HUD } from './gameplay/HUD.js';
import { EnemyManager } from './gameplay/EnemyManager.js';
import { ObstacleManager } from './gameplay/ObstacleManager.js';
import { HazardManager } from './gameplay/HazardManager.js';
import { LocalProfile } from './gameplay/LocalProfile.js';
import { MusicPlayer } from './core/MusicPlayer.js';
import { NetworkManager } from './network/NetworkManager.js';
import { StateInterpolator } from './network/StateInterpolator.js';
import { XRSessionManager } from './xr/XRSessionManager.js';
import { VRMenu } from './xr/VRMenu.js';
import { VRHud } from './xr/VRHud.js';
import { ParticleSystem } from './effects/ParticleSystem.js';
import { TracerPool } from './effects/TracerPool.js';
import { WorldDamageNumbers } from './effects/WorldDamageNumbers.js';
import {
  PlayerRole,
  EnemyType,
  ObstacleType,
  LevelTheme,
  getLevelTheme,
  ENEMY_CONFIGS,
  OBSTACLE_CONFIGS,
  VEHICLE_MAX_HEALTH,
  MAX_BOOST_CHARGE,
  BASE_SPEED,
  BOOST_SPEED,
  LATERAL_SPEED,
  MAX_LATERAL_OFFSET,
  DRIVER_FIRE_COOLDOWN_MS,
  PASSENGER_FIRE_COOLDOWN_MS,
  WEAPON_MAX_RANGE,
  HazardPhase,
  HazardType,
  StageOutcome,
  DifficultyId,
} from '@lucky-vr/common';
import type { StageDebriefPayload } from '@lucky-vr/common';

interface EnemyMeta {
  type: EnemyType;
  health: number;
  maxHealth: number;
  isTarget: boolean;
  disabled: boolean;
  configuration: string;
}

const AIR_TYPES = new Set<EnemyType>([EnemyType.Helicopter, EnemyType.Boss, EnemyType.Drone]);

/** Per-theme livery for the countdown, announcements and progress bar. */
const THEME_ACCENTS: Record<LevelTheme, [string, string]> = {
  [LevelTheme.Countryside]: ['#ff2d95', '#2ee6d6'],
  [LevelTheme.City]: ['#2ee6d6', '#8a2be2'],
  [LevelTheme.Tunnel]: ['#ffe066', '#ff2d95'],
  [LevelTheme.Desert]: ['#ff9a3a', '#b13d92'],
  [LevelTheme.Swamp]: ['#9ab784', '#3a5a54'],
};

class Game {
  private engine: Engine;
  private railPath: RailPath;
  private vehicle: Vehicle;
  private hud: HUD;
  private network: NetworkManager;
  private xrSession: XRSessionManager;
  private particles: ParticleSystem;
  private tracers: TracerPool;
  private damageNumbers: WorldDamageNumbers;
  private enemies: EnemyManager;
  private obstacles: ObstacleManager;
  private hazards: HazardManager;
  private music = new MusicPlayer();
  private profile = new LocalProfile();
  private difficulty = this.loadDifficulty();

  private vrMenu!: VRMenu;
  private vrHud = new VRHud();
  private role: PlayerRole | null = null;
  private isRunning = false;
  private inCountdown = false;
  private vrBriefingActive = false;
  private endInfo: string[] | null = null;
  private menuTime = 0;
  private currentTheme: LevelTheme = LevelTheme.Countryside;

  // Lobby
  private inLobby = false;
  private isSolo = false;
  private myReady = false;
  private myWantsRandom = false;
  private lobbyCode: string | null = null;
  private connectionPending = false;

  // Server state, interpolated for rendering
  private vehicleInterp = new StateInterpolator<{ t: number; lateral: number; speed: number }>();
  private enemyInterps = new Map<string, StateInterpolator<{ t: number; lateral: number; altitude: number }>>();
  private enemyMeta = new Map<string, EnemyMeta>();
  private bossAlive = false;
  private bossVitals: { health: number; maxHealth: number } | null = null;
  private targetTitle = 'TARGET';
  private targetState = 'unknown';
  private targetConfiguration = '';
  private currentBeatTitle = 'CONTACT';
  private currentObjective = 'Acquire the target';
  private stageTimeRemaining = 0;
  private takedownCount = 0;
  private lastDebrief: StageDebriefPayload | null = null;
  private lastBriefingRevision = -1;

  private serverHealth = VEHICLE_MAX_HEALTH;
  private boostCharge = MAX_BOOST_CHARGE;
  private teamScore = 0;
  private myCombo = 1;
  private level = 1;
  private levelTime = 0;
  private trackT = 0;

  // Feel
  private shakeAmp = 0;
  private gunCooldowns = { driver: 0, left: 0, right: 0 };
  private lastVehiclePos = new THREE.Vector3();
  private prevLateral = 0; // for deriving the gunner's view of the wheel
  private predictedLateral: number | null = null;
  private latestServerLateral = 0;
  private predictedTrackT: number | null = null;
  private predictedSpeed = BASE_SPEED;
  private latestServerTrackT = 0;

  constructor() {
    const params = new URLSearchParams(location.search);
    const isThemePreview = params.get('preview') === 'theme';
    const requestedLevel = Number(params.get('level'));
    const initialLevel = isThemePreview && Number.isFinite(requestedLevel)
      ? THREE.MathUtils.clamp(Math.floor(requestedLevel), 1, 5)
      : 1;
    const container = document.getElementById('app')!;
    this.level = initialLevel;
    this.currentTheme = getLevelTheme(initialLevel);
    this.railPath = RailPath.createLevel(initialLevel);
    this.engine = new Engine(container, this.railPath, this.currentTheme);
    this.vehicle = new Vehicle();
    this.hud = new HUD();
    this.network = new NetworkManager();
    this.particles = new ParticleSystem(this.engine.scene.effectsGroup);
    this.tracers = new TracerPool(this.engine.scene.effectsGroup);
    this.damageNumbers = new WorldDamageNumbers(this.engine.scene.effectsGroup);
    this.enemies = new EnemyManager(this.engine.scene.enemyGroup);
    this.obstacles = new ObstacleManager(this.engine.scene.enemyGroup);
    this.hazards = new HazardManager(this.engine.scene.effectsGroup);

    this.engine.scene.vehicleGroup.add(this.vehicle.mesh);
    this.vehicle.setPose(this.railPath, 0.002, 0, 0);

    // In VR the camera + controllers ride the cockpit (the camera mount)
    this.xrSession = new XRSessionManager(
      this.engine.renderer,
      this.engine.input,
      this.vehicle.cameraMount
    );

    // In-headset menu (the DOM UI doesn't exist inside WebXR)
    this.vrMenu = new VRMenu({
      exit: () => this.xrSession.exitVR(),
      solo: () => this.startSolo(),
      random: () => this.startRandom(false),
      create: () => this.createLobby(),
      joinCode: (code) => this.joinLobbyByCode(code),
      drive: () => this.selectRole('driver'),
      gun: () => this.selectRole('passenger'),
      seatRandom: () => this.selectRole('random'),
      ready: () => this.sendLobbyReady(),
      skip: () => this.network.sendSkipPresentation(),
    });
    // Modal panels are eye-relative so different runtime floor/head origins
    // cannot put them under the vehicle. Live data is physically in the dash.
    this.engine.renderer.camera.add(this.vrMenu.group);
    this.vehicle.dashboardAnchor.add(this.vrHud.mesh);
    this.xrSession.onSessionStart = () => {
      const xr = this.engine.renderer.renderer.xr;
      this.vrMenu.attachControllers([xr.getController(0), xr.getController(1)]);
    };
    this.hud.onStatus = (text) => {
      if (!this.network.sessionId) this.vrMenu.setStatus(['GLITCH PURSUIT', text]);
    };

    this.setupUI();
    this.setupNetwork();
    this.setupEngine();
    this.checkVR();

    // menu music: try immediately, and retry on the first gesture if the
    // browser's autoplay policy blocked it (policies always win until then)
    this.music.playMenu();
    const unlockMusic = () => {
      if (!this.isRunning) this.music.playMenu();
      window.removeEventListener('pointerdown', unlockMusic);
      window.removeEventListener('keydown', unlockMusic);
    };
    window.addEventListener('pointerdown', unlockMusic);
    window.addEventListener('keydown', unlockMusic);

    // music on/off toggle (persisted)
    const musicBtn = document.getElementById('btn-music')!;
    const paintMusicBtn = () => {
      musicBtn.textContent = this.music.muted ? '🔇' : '🔊';
      musicBtn.classList.toggle('muted', this.music.muted);
    };
    paintMusicBtn();
    musicBtn.addEventListener('click', () => {
      this.music.toggleMuted();
      paintMusicBtn();
    });

    if (isThemePreview) {
      this.role = PlayerRole.Driver;
      this.isRunning = true;
      this.trackT = 0.16;
      this.stageTimeRemaining = 120;
      this.currentBeatTitle = 'VISUAL CHECK';
      this.currentObjective = `${this.currentTheme} material and lighting preview`;
      this.vehicle.setPose(this.railPath, this.trackT, 0, 0);
      this.hud.showHUD();
    }
    // dev/testing hook: ?autoplay=solo|coop starts a run immediately
    const autoplay = params.get('autoplay');
    if (!isThemePreview && autoplay === 'solo') this.startSolo();
    if (!isThemePreview && autoplay === 'coop') this.startRandom(true);
    // share link: ?join=CODE drops you straight into a friend's lobby
    const joinCode = params.get('join');
    if (joinCode) this.joinLobbyByCode(joinCode.toUpperCase());

    (window as any).__game = this;
  }

  private async checkVR(): Promise<void> {
    const vrSupported = await this.xrSession.checkSupport();
    const vrBtn = document.getElementById('btn-vr');
    if (vrBtn && vrSupported) {
      vrBtn.style.display = 'inline-block';
    }
  }

  private setupUI(): void {
    const click = (id: string, handler: () => void) => {
      document.getElementById(id)?.addEventListener('click', () => {
        this.engine.audio.init().then(() => this.engine.audio.uiClick());
        handler();
      });
    };

    click('btn-solo', () => this.startSolo());
    click('btn-random', () => this.startRandom(false));
    click('btn-create', () => this.createLobby());
    click('btn-join-code', () => {
      const input = document.getElementById('code-input') as HTMLInputElement;
      const code = input.value.trim().toUpperCase();
      if (code.length >= 4) this.joinLobbyByCode(code);
      else this.hud.setStatus('Enter the 5-letter lobby code');
    });
    click('btn-vr', () => this.xrSession.enterVR());
    click('btn-skip', () => this.network.sendSkipPresentation());
    click('btn-bank', () => this.bankCampaignScore());
    click('btn-diff-rookie', () => this.setDifficulty(DifficultyId.Rookie));
    click('btn-diff-agent', () => this.setDifficulty(DifficultyId.Agent));
    click('btn-diff-veteran', () => this.setDifficulty(DifficultyId.Veteran));
    this.paintDifficulty();

    click('btn-role-driver', () => this.selectRole('driver'));
    click('btn-role-gunner', () => this.selectRole('passenger'));
    click('btn-role-random', () => this.selectRole('random'));
    click('btn-ready', () => this.sendLobbyReady());
    click('btn-copy', () => this.copyShareLink());

    click('btn-again', () => {
      this.hud.hideEndScreen();
      this.hud.announce(this.role && !this.isRunning ? 'GET READY' : '');
      this.network.sendReady();
    });

    (document.getElementById('code-input') as HTMLInputElement | null)
      ?.addEventListener('input', (e) => {
        const input = e.target as HTMLInputElement;
        input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      });
  }

  private devStartLevel(): number | undefined {
    const levelParam = Number(new URLSearchParams(location.search).get('level'));
    return Number.isFinite(levelParam) && levelParam > 1 ? levelParam : undefined;
  }

  private selectedDifficulty(): DifficultyId {
    return this.difficulty;
  }

  private loadDifficulty(): DifficultyId {
    const requested = new URLSearchParams(location.search).get('difficulty');
    if (Object.values(DifficultyId).includes(requested as DifficultyId)) return requested as DifficultyId;
    try {
      const saved = localStorage.getItem('glitch-pursuit.difficulty.v1');
      if (Object.values(DifficultyId).includes(saved as DifficultyId)) return saved as DifficultyId;
    } catch {
      // Local storage is optional.
    }
    return DifficultyId.Agent;
  }

  private setDifficulty(difficulty: DifficultyId): void {
    this.difficulty = difficulty;
    try { localStorage.setItem('glitch-pursuit.difficulty.v1', difficulty); } catch { /* optional */ }
    this.paintDifficulty();
  }

  private paintDifficulty(): void {
    for (const difficulty of Object.values(DifficultyId)) {
      document.getElementById(`btn-diff-${difficulty}`)?.classList.toggle('selected', difficulty === this.difficulty);
    }
  }

  private async startSolo(): Promise<void> {
    if (!this.beginConnection('STARTING SOLO RUN')) return;
    this.hud.setStatus('Starting engine...');
    try {
      await this.engine.audio.init();
      await this.network.joinSolo(this.devStartLevel(), this.selectedDifficulty());
      this.isSolo = true;
      this.network.sendReady();
    } catch (e) {
      this.connectFailed(e);
    } finally {
      this.finishConnection();
    }
  }

  /** Public matchmaking. autoReady is used by tests/bots. */
  private async startRandom(autoReady: boolean): Promise<void> {
    if (!this.beginConnection('SEARCHING FOR CREW')) return;
    this.hud.setStatus('Finding a public game...');
    try {
      await this.network.joinRandom(this.devStartLevel(), this.selectedDifficulty());
      this.isSolo = false;
      this.showLobby('PUBLIC');
      if (autoReady) this.sendLobbyReady();
      else this.hud.setStatus('Pick a seat, then hit READY');
    } catch (e) {
      this.connectFailed(e);
    } finally {
      this.finishConnection();
    }
  }

  private async createLobby(): Promise<void> {
    if (!this.beginConnection('OPENING PRIVATE LOBBY')) return;
    const code = generateLobbyCode();
    this.hud.setStatus('Opening lobby...');
    try {
      await this.network.createLobby(code, this.devStartLevel(), this.selectedDifficulty());
      this.isSolo = false;
      this.showLobby(code);
      this.hud.setStatus('Share the code (or link) with a friend');
    } catch (e) {
      this.connectFailed(e);
    } finally {
      this.finishConnection();
    }
  }

  private async joinLobbyByCode(code: string): Promise<void> {
    if (!this.beginConnection(`JOINING ${code}`)) return;
    this.hud.setStatus(`Joining lobby ${code}...`);
    try {
      await this.network.joinByCode(code);
      this.isSolo = false;
      this.showLobby(code);
      this.hud.setStatus('Pick a seat, then hit READY');
    } catch (e) {
      console.error('Join failed:', e);
      this.hud.setStatus(`Lobby ${code} not found — check the code`);
    } finally {
      this.finishConnection();
    }
  }

  private beginConnection(vrStatus: string): boolean {
    if (this.connectionPending || this.network.sessionId) return false;
    this.connectionPending = true;
    this.vrMenu.setBusy(true);
    this.vrMenu.setStatus(['GLITCH PURSUIT', vrStatus, 'PLEASE WAIT']);
    for (const id of ['btn-solo', 'btn-random', 'btn-create', 'btn-join-code']) {
      (document.getElementById(id) as HTMLButtonElement | null)?.setAttribute('disabled', 'true');
    }
    return true;
  }

  private finishConnection(): void {
    this.connectionPending = false;
    this.vrMenu.setBusy(false);
    for (const id of ['btn-solo', 'btn-random', 'btn-create', 'btn-join-code']) {
      (document.getElementById(id) as HTMLButtonElement | null)?.removeAttribute('disabled');
    }
  }

  private connectFailed(e: unknown): void {
    console.error('Failed to connect:', e);
    this.hud.setStatus('Connection failed — is the server running?');
  }

  private showLobby(codeLabel: string): void {
    this.inLobby = true;
    this.myReady = false;
    this.myWantsRandom = false;
    this.lobbyCode = codeLabel === 'PUBLIC' ? null : codeLabel;
    document.getElementById('menu-buttons')!.style.display = 'none';
    document.getElementById('lobby')!.style.display = 'flex';
    document.getElementById('lobby-code')!.textContent = codeLabel;
    (document.getElementById('btn-copy') as HTMLElement).style.display =
      this.lobbyCode ? 'inline-block' : 'none';
  }

  private selectRole(role: 'driver' | 'passenger' | 'random'): void {
    if (this.myReady) return;
    this.myWantsRandom = role === 'random';
    this.network.sendSelectRole(role);
    this.updateLobbyButtons();
  }

  private sendLobbyReady(): void {
    if (this.myReady || !this.network.sessionId) return;
    this.myReady = true;
    this.network.sendReady();
    const btn = document.getElementById('btn-ready') as HTMLButtonElement;
    btn.textContent = 'Waiting...';
    this.hud.setStatus('Waiting for your partner...');
  }

  private copyShareLink(): void {
    if (!this.lobbyCode) return;
    const link = `${location.origin}${location.pathname}?join=${this.lobbyCode}`;
    navigator.clipboard?.writeText(link).then(() => {
      this.hud.setStatus('Link copied — send it to a friend!');
    }).catch(() => {
      this.hud.setStatus(`Share this link: ${link}`);
    });
  }

  private bankCampaignScore(): void {
    if (!this.lastDebrief?.campaignComplete) return;
    const input = document.getElementById('rank-name') as HTMLInputElement;
    const entries = this.profile.bank(input.value, this.lastDebrief.score, this.lastDebrief.grade);
    this.hud.showLocalRanking(entries, input.value, true);
  }

  private updateLobbyButtons(): void {
    document.getElementById('btn-role-driver')?.classList.toggle(
      'selected', !this.myWantsRandom && this.role === PlayerRole.Driver);
    document.getElementById('btn-role-gunner')?.classList.toggle(
      'selected', !this.myWantsRandom && this.role === PlayerRole.Passenger);
    document.getElementById('btn-role-random')?.classList.toggle('selected', this.myWantsRandom);

    const seat = this.myWantsRandom ? 'seat-random'
      : this.role === PlayerRole.Driver ? 'seat-drive'
      : this.role === PlayerRole.Passenger ? 'seat-gun'
      : null;
    this.vrMenu.setSelected(seat, this.myReady);
  }

  /** Live seat cards: who holds each seat, and whether they're ready. */
  private updateLobbySlots(state: any): void {
    const slots: Record<string, { holder: HTMLElement; card: HTMLElement }> = {
      [PlayerRole.Driver]: {
        holder: document.getElementById('slot-driver')!,
        card: document.getElementById('slot-driver-card')!,
      },
      [PlayerRole.Passenger]: {
        holder: document.getElementById('slot-gunner')!,
        card: document.getElementById('slot-gunner-card')!,
      },
    };

    for (const slot of Object.values(slots)) {
      slot.holder.textContent = 'waiting...';
      slot.card.classList.remove('mine');
    }

    const vrLines: Record<string, string> = {
      [PlayerRole.Driver]: 'DRIVER: —',
      [PlayerRole.Passenger]: 'GUNNER: —',
    };
    state.players?.forEach((player: any) => {
      const slot = slots[player.role];
      if (!slot) return;
      const mine = player.sessionId === this.network.sessionId;
      slot.holder.innerHTML =
        `${mine ? 'YOU' : 'PARTNER'}${player.wantsRandom ? ' 🎲' : ''}` +
        `${player.ready ? ' <span class="ready-tag">✔ READY</span>' : ''}`;
      if (mine) slot.card.classList.add('mine');
      vrLines[player.role] =
        `${player.role === PlayerRole.Driver ? 'DRIVER' : 'GUNNER'}: ` +
        `${mine ? 'YOU' : 'PARTNER'}${player.ready ? ' *READY*' : ''}`;
    });

    this.vrPanelSeatLines = [vrLines[PlayerRole.Driver], vrLines[PlayerRole.Passenger]];
    this.updateLobbyButtons();
  }

  private vrPanelSeatLines: string[] = [];

  /** VR panel copy for every joined-room state: lobby, solo, and end screens. */
  private updateVrPanel(): void {
    if (this.endInfo) {
      this.vrMenu.setStatus([
        this.endInfo[0],
        this.endInfo[1],
        this.myReady
          ? (this.isSolo ? 'restarting...' : 'waiting for partner...')
          : 'READY = play again',
      ]);
    } else if (this.isSolo) {
      this.vrMenu.setStatus(['SOLO RUN', 'pull READY to launch']);
    } else {
      this.vrMenu.setStatus([
        `LOBBY ${this.lobbyCode ?? 'PUBLIC'}`,
        this.myReady ? 'waiting for partner...' : 'pick a seat, then READY',
        ...this.vrPanelSeatLines,
      ]);
    }
  }

  private setupNetwork(): void {
    this.network.onRoleAssigned = (role) => {
      this.role = role;
      this.vehicle.setSeat(role === PlayerRole.Driver);
      this.xrSession.setRole(role);
      this.updateLobbyButtons();
      const label = role === PlayerRole.Driver ? 'DRIVER — steer & hood gun' : 'GUNNER — dual guns, free aim';
      this.hud.setStatus(`You are the ${label}`);
    };

    this.network.onStateChange = (state: any) => this.readState(state);

    this.network.onStageBriefing = (data) => {
      this.inCountdown = true;
      this.vrBriefingActive = true;
      this.lastDebrief = null;
      this.ensureTheme(data.level);
      this.targetTitle = data.targetTitle;
      this.hud.showBriefing(data);
      this.hud.setTargetLabel(data.targetTitle);
      this.vrHud.setVisible(false);
      this.vrMenu.setStatus([
        `OP ${data.level}: ${data.title.toUpperCase()}`,
        data.location.toUpperCase(),
        `TARGET ${data.targetTitle}`,
        `${data.targetBounty.toLocaleString()} CR`,
      ]);
    };

    this.network.onCountdown = (count, level) => {
      // countdown = the run is happening: hide menus now
      this.inCountdown = true;
      this.vrBriefingActive = false;
      this.endInfo = null;
      // the world + livery switch as the countdown begins, not at GO
      this.ensureTheme(level);
      this.hud.hideBriefing();
      this.engine.audio.countdownBeep(count === 0);
      this.hud.announce(count > 0 ? String(count) : 'GO!');
    };

    this.network.onGameStart = (level) => {
      this.inCountdown = false;
      this.vrBriefingActive = false;
      this.level = level;
      this.resetInterpolation();
      this.ensureTheme(level);

      this.hud.showHUD();
      this.hud.showBossBar(false);
      this.isRunning = true;
      this.engine.audio.startEngine();
      this.music.playForLevel(level);
      this.hud.announce(`LEVEL ${level}`);
      this.hud.setTargetLabel(this.targetTitle);
    };

    this.network.onObjectiveChanged = (data) => {
      this.currentBeatTitle = data.beatTitle;
      this.currentObjective = data.objective;
      this.hud.announce(data.beatTitle.toUpperCase());
    };

    this.network.onBeatRecovery = (data) => {
      this.hud.announce(`FIELD REPAIR +${Math.round(data.amount)}`);
    };

    this.network.onTargetContact = () => {
      this.hud.showBossBar(true);
      this.hud.announce(`CONTACT // ${this.targetTitle}`);
      this.engine.audio.countdownBeep(true);
    };

    this.network.onTargetStateChanged = (data) => {
      this.targetState = data.state;
      if (data.state === 'disabled') this.hud.announce('TARGET DISABLED');
      else if (data.state === 'reacquired') this.hud.announce('TARGET REACQUIRED');
      else if (data.state === 'separated') this.hud.announce('VISUAL LOST');
    };

    this.network.onTargetConfigurationChanged = (data) => {
      this.targetConfiguration = data.configuration;
      if (data.configuration === 'exposed') this.hud.announce('COMPONENTS EXPOSED');
      if (data.configuration === 'critical') this.hud.announce('TARGET CRITICAL');
    };

    this.network.onTargetComponentBroken = (data) => {
      this.hud.announce(`${data.label} BROKEN`);
      this.engine.audio.hit();
    };

    this.network.onHazardWarning = (data) => {
      this.hud.warnHazard(data.label);
      this.engine.audio.countdownBeep(false);
    };

    this.network.onHazardCommitted = (data) => {
      this.hud.warnHazard(data.label, true);
      this.engine.audio.countdownBeep(true);
    };

    this.network.onHazardResolved = (data) => {
      this.hud.clearHazard(Boolean(data.hit), Boolean(data.nearMiss));
      if (data.hit) {
        this.engine.audio.vehicleHit();
        this.hud.flashVignette();
        this.shakeAmp = Math.min(0.3, this.shakeAmp + 0.16);
      }
    };

    this.network.onShotFired = (d) => {
      // our own shots are already rendered locally with zero latency
      if (d.shooter === this.network.sessionId) return;
      this.tracers.fire(
        new THREE.Vector3(d.origin.x, d.origin.y, d.origin.z),
        new THREE.Vector3(d.end.x, d.end.y, d.end.z),
        0xaaddff
      );
      this.engine.audio.partnerShoot();
    };

    this.network.onHitConfirm = (d) => {
      const point = new THREE.Vector3(d.point.x, d.point.y, d.point.z);
      this.particles.emit(point, 6, new THREE.Color(0xffaa33), 4, 0.25);
      this.enemies.flashHit(d.enemyId);
      this.engine.audio.hit();
      if (d.shooter === this.network.sessionId) {
        this.hud.flashHitmarker();
        if (this.xrSession.isInVR) {
          this.damageNumbers.spawn(point, d.damage, Boolean(d.componentBroken));
        }
      }
      // per-shot damage number floating off the enemy
      const screen = this.projectToScreen(point);
      if (screen) {
        this.hud.damagePopup(screen.x, screen.y, d.damage);
      }
    };

    this.network.onObstacleDestroyed = (d) => {
      const point = new THREE.Vector3(d.point.x, d.point.y, d.point.z);
      this.obstacles.remove(d.obstacleId);
      if (d.repairAmount !== undefined) {
        this.particles.emit(point, 26, new THREE.Color(0x34f5e5), 7, 0.55);
        this.engine.audio.countdownBeep(true);
        this.hud.announce(d.repairAmount > 0
          ? `REPAIR CACHE +${Math.round(d.repairAmount)}`
          : 'REPAIR CACHE // HEALTH FULL');
      } else if (d.collided) {
        // we drove into it
        this.particles.emitExplosion(point);
        this.engine.audio.vehicleHit();
        this.engine.audio.explosion();
        this.hud.flashVignette();
        this.shakeAmp = Math.min(0.3, this.shakeAmp + 0.14);
      } else {
        // someone shot it out of the way
        this.particles.emitExplosion(point);
        this.engine.audio.explosion();
        const screen = this.projectToScreen(point);
        if (screen && d.score > 0) {
          this.hud.scorePopup(screen.x, screen.y, `+${d.score}`);
        }
      }
    };

    this.network.onEnemyDestroyed = (d) => {
      const point = new THREE.Vector3(d.point.x, d.point.y, d.point.z);
      const isBoss = Boolean(this.enemyMeta.get(d.enemyId)?.isTarget);
      this.particles.emitExplosion(point);
      this.engine.audio.explosion(isBoss);
      this.shakeAmp = Math.min(0.3, this.shakeAmp + (isBoss ? 0.25 : 0.08));

      const screen = this.projectToScreen(point);
      if (screen) {
        // d.score is already the combo-multiplied total
        this.hud.scorePopup(screen.x, screen.y, `+${d.score.toLocaleString()}`, isBoss);
      }
      if (isBoss) {
        this.hud.announce('TARGET DISABLED');
      }
    };

    this.network.onEnemyTelegraph = (d) => {
      const meta = this.enemyMeta.get(d.enemyId);
      if (!meta) return;
      this.enemies.telegraph(
        d.enemyId,
        d.durationMs || ENEMY_CONFIGS[meta.type].telegraphMs,
        new THREE.Vector3(d.target.x, d.target.y, d.target.z),
        this.railPath.getQuaternion(d.targetT),
      );
    };

    this.network.onEnemyFired = (d) => {
      const origin = new THREE.Vector3(d.origin.x, d.origin.y, d.origin.z);
      const lockedTarget = new THREE.Vector3(d.target.x, d.target.y, d.target.z);
      const impact = d.hit
        ? this.lastVehiclePos.clone().add(new THREE.Vector3(0, 1.1, 0))
        : lockedTarget;
      const toUs = lockedTarget.clone().sub(origin).normalize();
      this.enemies.clearTelegraph(d.enemyId);

      // visible burst: muzzle flash at the shooter, three staggered fat
      // tracers to the lane locked during aim, then sparks at the actual impact.
      this.particles.emitMuzzleFlash(origin, toUs);
      this.particles.emit(origin, 5, new THREE.Color(0xff5522), 3, 0.2);
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          const jitter = new THREE.Vector3(
            (Math.random() - 0.5) * 0.5,
            (Math.random() - 0.5) * 0.3,
            (Math.random() - 0.5) * 0.5
          );
          this.tracers.fire(origin, lockedTarget.clone().add(jitter), 0xff3344, 0.14, 220);
        }, i * 65);
      }
      this.particles.emit(impact, 8, new THREE.Color(0xff3344), 5, 0.3);

      this.engine.audio.enemyShoot();
      if (d.hit) {
        this.engine.audio.vehicleHit();
        this.hud.flashVignette();
        this.shakeAmp = Math.min(0.3, this.shakeAmp + 0.1);
      } else {
        this.hud.announce('SHOT EVADED');
      }
    };

    this.network.onBossPhaseChange = (d) => {
      this.hud.showBossBar(true);
      this.hud.announce(d.phase === 1 ? '⚠ GUNSHIP ⚠' : d.phase === 2 ? 'GUNSHIP ENRAGED' : 'FINISH IT!');
      this.engine.audio.countdownBeep(true);
    };

    this.network.onLevelComplete = (d) => {
      if (this.lastDebrief) return;
      if (d.bossEscaped) this.hud.announce('TARGET ESCAPED');
      this.endRun(true, d.level, d.bossEscaped);
    };

    this.network.onStageDebrief = (data) => {
      this.lastDebrief = data;
      if (data.outcome !== StageOutcome.VehicleDestroyed) {
        this.endRun(true, data.level, data.outcome !== StageOutcome.Captured, data);
      }
    };

    this.network.onCampaignComplete = () => {
      this.hud.announce('CAMPAIGN COMPLETE');
    };

    this.network.onGameOver = () => {
      this.endRun(false, this.level, false, this.lastDebrief ?? undefined);
    };

    this.network.onPartnerDisconnected = () => {
      this.hud.announce('PARTNER LOST');
      this.hud.setStatus('Partner disconnected — waiting for reconnect...');
    };

    this.network.onPartnerReconnected = () => {
      this.hud.announce('PARTNER BACK');
      this.hud.setStatus('');
    };
  }

  private endRun(
    won: boolean,
    level: number,
    bossEscaped = false,
    debrief?: StageDebriefPayload,
  ): void {
    this.isRunning = false;
    this.inCountdown = false;
    this.vrBriefingActive = false;
    this.vrHud.setVisible(false);
    this.damageNumbers.clear();
    this.myReady = false;
    this.engine.audio.stopEngine();
    this.music.playMenu();
    if (!won) {
      this.engine.audio.explosion(true);
      this.shakeAmp = 0.3;
    }
    this.hud.showEndScreen(won, {
      score: debrief?.score ?? this.teamScore,
      level,
      time: debrief?.elapsedSeconds ?? this.levelTime,
      bossEscaped,
      debrief,
    });
    if (debrief?.campaignComplete) {
      this.hud.showLocalRanking(this.profile.scores(), this.profile.agentName);
    }

    // mirror the end screen onto the VR panel (title renders as neon header)
    const mins = Math.floor(this.levelTime / 60);
    const secs = Math.floor(this.levelTime % 60).toString().padStart(2, '0');
    this.endInfo = [
      !won ? 'WRECKED'
        : debrief?.campaignComplete && debrief.outcome === StageOutcome.Captured ? 'CAMPAIGN COMPLETE'
        : debrief?.outcome === StageOutcome.Captured ? 'TARGET CAPTURED'
        : 'TARGET ESCAPED',
      `SCORE ${(debrief?.score ?? this.teamScore).toLocaleString()}   TIME ${mins}:${secs}${bossEscaped ? '   (target escaped)' : ''}`,
    ];
    this.updateVrPanel();
  }

  /** Swap world + HUD livery to the given level's theme (idempotent). */
  private ensureTheme(level: number): void {
    this.level = level;
    const theme = getLevelTheme(level);
    const [primary, secondary] = THEME_ACCENTS[theme];
    this.hud.setLevelLabel(level, theme);
    this.hud.setThemeAccent(primary, secondary);
    if (theme !== this.currentTheme) {
      this.currentTheme = theme;
      this.railPath = RailPath.createLevel(level);
      this.engine.scene.buildEnvironment(this.railPath, theme);
    }
  }

  private resetInterpolation(): void {
    this.vehicleInterp = new StateInterpolator();
    this.predictedLateral = null;
    this.latestServerLateral = 0;
    this.predictedTrackT = null;
    this.predictedSpeed = BASE_SPEED;
    this.latestServerTrackT = 0;
    this.damageNumbers.clear();
    this.enemyInterps.clear();
    this.enemyMeta.clear();
    this.enemies.clear();
    this.obstacles.clear();
    this.hazards.clear();
    this.bossAlive = false;
    this.targetConfiguration = '';
    this.targetState = 'unknown';
  }

  /** Pull everything we render from the latest server patch. */
  private readState(state: any): void {
    if (state.phase === 'briefing') {
      const revision = state.presentationRevision ?? 0;
      if (revision !== this.lastBriefingRevision) {
        this.lastBriefingRevision = revision;
        if (!document.getElementById('mission-screen')!.classList.contains('show')) {
          this.inCountdown = true;
          this.vrBriefingActive = true;
          this.ensureTheme(state.currentLevel ?? this.level);
          this.targetTitle = state.targetTitle || 'TARGET';
          this.hud.showBriefing({
            level: state.currentLevel,
            stageId: state.stageId,
            title: state.stageTitle,
            location: state.stageLocation,
            briefing: state.briefing,
            targetTitle: state.targetTitle,
            targetBounty: state.targetBounty,
            timeLimitSeconds: state.stageTimeRemaining,
          });
          this.vrMenu.setStatus([
            `OP ${state.currentLevel}: ${String(state.stageTitle || 'NEW OPERATION').toUpperCase()}`,
            String(state.stageLocation || '').toUpperCase(),
            `TARGET ${String(state.targetTitle || 'UNKNOWN').toUpperCase()}`,
            'PULL TRIGGER TO SKIP',
          ]);
        }
      }
      this.hud.updateBriefingVotes(state.skipVotes ?? 0, state.skipVotesRequired ?? 1);
    }
    if (this.inLobby && !this.isRunning) {
      this.updateLobbySlots(state);
    }
    if (this.network.sessionId && !this.isRunning && !this.inCountdown) {
      this.updateVrPanel();
    }

    if (state.vehicle) {
      const serverLateral = state.vehicle.lateralOffset as number;
      const serverTrackT = state.vehicle.t as number;
      const serverSpeed = state.vehicle.speed as number;
      this.vehicleInterp.pushSnapshot({
        t: serverTrackT,
        lateral: serverLateral,
        speed: serverSpeed,
      });
      this.latestServerLateral = serverLateral;
      if (this.predictedLateral === null || Math.abs(serverLateral - this.predictedLateral) > 1.75) {
        this.predictedLateral = serverLateral;
      } else if (this.role === PlayerRole.Driver && this.xrSession.isInVR) {
        // Reconcile gently to the authority without feeding the 20 Hz patch cadence
        // back into the driver's view.
        this.predictedLateral += (serverLateral - this.predictedLateral) * 0.12;
      }

      this.latestServerTrackT = serverTrackT;
      if (this.predictedTrackT === null || Math.abs(serverTrackT - this.predictedTrackT) > 0.02) {
        this.predictedTrackT = serverTrackT;
        this.predictedSpeed = serverSpeed;
      } else if (this.xrSession.isInVR) {
        // XR renders the vehicle at the present estimate instead of the 120 ms
        // interpolation history used by desktop. Small authority errors are
        // absorbed over several packets so the cockpit never snaps.
        this.predictedTrackT += (serverTrackT - this.predictedTrackT) * 0.14;
        this.predictedSpeed += (serverSpeed - this.predictedSpeed) * 0.35;
      } else {
        this.predictedTrackT = serverTrackT;
        this.predictedSpeed = serverSpeed;
      }
      this.serverHealth = state.vehicle.health;
      this.boostCharge = state.vehicle.boostCharge ?? this.boostCharge;
      this.trackT = serverTrackT;
      // windscreen cracks up as damage accumulates (clears on fresh runs)
      this.vehicle.setWindshieldDamage(
        Math.min(4, Math.floor((1 - state.vehicle.health / VEHICLE_MAX_HEALTH) * 4.999))
      );
    }

    this.level = state.currentLevel ?? this.level;
    this.levelTime = state.levelTimer ?? this.levelTime;
    this.stageTimeRemaining = state.stageTimeRemaining ?? this.stageTimeRemaining;
    this.currentBeatTitle = state.currentBeatTitle || this.currentBeatTitle;
    this.currentObjective = state.objective || this.currentObjective;
    this.targetTitle = state.targetTitle || this.targetTitle;
    this.targetState = state.targetState || this.targetState;
    this.targetConfiguration = state.targetConfiguration || this.targetConfiguration;

    let teamScore = 0;
    let takedowns = 0;
    state.players?.forEach((player: any) => {
      teamScore += player.score;
      takedowns += player.takedowns ?? 0;
      if (player.sessionId === this.network.sessionId) {
        this.myCombo = player.comboMultiplier;
      }
    });
    this.teamScore = teamScore;
    this.takedownCount = takedowns;

    const targetComponents: Array<{ id: string; exposed: boolean; broken: boolean }> = [];
    state.targetComponents?.forEach((component: any) => {
      targetComponents.push({
        id: component.id,
        exposed: component.exposed,
        broken: component.broken,
      });
    });

    // --- enemies ---
    const ids = new Set<string>();
    let bossSeen = false;
    state.enemies?.forEach((enemy: any) => {
      ids.add(enemy.id);
      this.enemyMeta.set(enemy.id, {
        type: enemy.enemyType as EnemyType,
        health: enemy.health,
        maxHealth: enemy.maxHealth,
        isTarget: Boolean(enemy.isTarget || enemy.enemyType === EnemyType.ArmoredTarget),
        disabled: Boolean(enemy.disabled),
        configuration: enemy.targetConfiguration || '',
      });

      let interp = this.enemyInterps.get(enemy.id);
      if (!interp) {
        interp = new StateInterpolator();
        this.enemyInterps.set(enemy.id, interp);
      }
      interp.pushSnapshot({
        t: enemy.t,
        lateral: enemy.lateralOffset,
        altitude: enemy.altitude,
      });

      if (enemy.isTarget || (enemy.enemyType as EnemyType) === EnemyType.ArmoredTarget || (enemy.enemyType as EnemyType) === EnemyType.Boss) {
        bossSeen = true;
        this.bossVitals = { health: enemy.health, maxHealth: enemy.maxHealth };
        this.hud.updateBossHealth(enemy.health, enemy.maxHealth);
      }
    });

    for (const id of [...this.enemyInterps.keys()]) {
      if (!ids.has(id)) {
        this.enemyInterps.delete(id);
        this.enemyMeta.delete(id);
      }
    }
    this.enemies.sync(ids, (id) => this.enemyMeta.get(id)!.type);
    for (const [id, meta] of this.enemyMeta) {
      if (meta.isTarget) {
        this.enemies.setTargetVisual(id, meta.configuration, meta.disabled, targetComponents);
      }
    }
    this.bossAlive = bossSeen;
    this.hud.showBossBar(bossSeen && this.isRunning);
    this.hud.setTargetLabel(this.targetTitle);

    // --- obstacles (static — posed once from their rail position) ---
    const obstacleIds = new Set<string>();
    const obstacleInfo = new Map<string, { type: ObstacleType; t: number; lateral: number; health: number }>();
    state.obstacles?.forEach((o: any) => {
      obstacleIds.add(o.id);
      obstacleInfo.set(o.id, {
        type: o.obstacleType as ObstacleType,
        t: o.t,
        lateral: o.lateralOffset,
        health: o.health,
      });
    });
    this.obstacles.sync(obstacleIds, (id) => {
      const info = obstacleInfo.get(id)!;
      return {
        type: info.type,
        position: this.railPath.getPosition(info.t, info.lateral),
        quaternion: this.railPath.getQuaternion(info.t),
        health: info.health,
        maxHealth: OBSTACLE_CONFIGS[info.type].health,
      };
    });

    // --- telegraphed authored hazards ---
    const hazardStates = new Map<string, {
      hazardType: HazardType;
      phase: HazardPhase;
      position: THREE.Vector3;
      quaternion: THREE.Quaternion;
      radius: number;
      phaseRemaining: number;
    }>();
    state.hazards?.forEach((hazard: any) => {
      const phase = hazard.phase as HazardPhase;
      hazardStates.set(hazard.id, {
        hazardType: hazard.hazardType as HazardType,
        phase,
        position: this.railPath.getPosition(hazard.t, hazard.lateralOffset, 0.02),
        quaternion: this.railPath.getQuaternion(hazard.t),
        radius: hazard.radius,
        phaseRemaining: hazard.phaseRemaining,
      });
    });
    this.hazards.sync(hazardStates);
  }

  private setupEngine(): void {
    this.engine.setCallbacks(
      (dt) => this.fixedUpdate(dt),
      (dt) => this.frameUpdate(dt)
    );
    this.engine.start();
  }

  /** 60Hz: sample input, send to server, render own gunfire immediately. */
  private fixedUpdate(dt: number): void {
    if (!this.isRunning || !this.role) return;

    this.gunCooldowns.driver = Math.max(0, this.gunCooldowns.driver - dt * 1000);
    this.gunCooldowns.left = Math.max(0, this.gunCooldowns.left - dt * 1000);
    this.gunCooldowns.right = Math.max(0, this.gunCooldowns.right - dt * 1000);

    if (this.role === PlayerRole.Driver) {
      const input = this.engine.input.getDriverInput();
      if (this.xrSession.isInVR) {
        const at = this.predictedLateral ?? this.latestServerLateral;
        this.predictedLateral = THREE.MathUtils.clamp(
          at + input.lateral * LATERAL_SPEED * dt,
          -MAX_LATERAL_OFFSET,
          MAX_LATERAL_OFFSET,
        );
      }
      this.network.sendDriverInput(input);
      if (input.shoot && this.gunCooldowns.driver <= 0) {
        this.gunCooldowns.driver = DRIVER_FIRE_COOLDOWN_MS;
        this.localShot(input.aimDir, 'right');
      }
    } else {
      const input = this.engine.input.getPassengerInput();
      this.network.sendPassengerInput(input);
      if (input.shootL && this.gunCooldowns.left <= 0) {
        this.gunCooldowns.left = PASSENGER_FIRE_COOLDOWN_MS;
        this.localShot(input.aimDirL, 'left');
      }
      if (input.shootR && this.gunCooldowns.right <= 0) {
        this.gunCooldowns.right = PASSENGER_FIRE_COOLDOWN_MS;
        this.localShot(input.aimDirR, 'right');
      }
    }
  }

  /** Zero-latency muzzle flash + tracer for our own gun; server confirms hits. */
  private localShot(aimDir: { x: number; y: number; z: number }, hand: 'left' | 'right'): void {
    const cam = this.engine.renderer.camera;
    const dir = new THREE.Vector3(aimDir.x, aimDir.y, aimDir.z).normalize();

    let muzzle: THREE.Vector3;
    let end: THREE.Vector3;

    const handPos = this.xrSession.getHandWorldPosition(hand);
    if (handPos) {
      // VR: everything derives from the hand pose — the tracer leaves the
      // pistol's barrel tip and flies along its ray. (Never use the head
      // camera here: its XR world transform is only valid mid-render.)
      muzzle = handPos.clone().addScaledVector(dir, 0.3);
      end = handPos.clone().addScaledVector(dir, WEAPON_MAX_RANGE);
    } else {
      // desktop: from below the eye, offset per gun so dual-wield reads
      const origin = cam.getWorldPosition(new THREE.Vector3());
      const side = new THREE.Vector3().crossVectors(dir, cam.up).normalize();
      muzzle = origin.clone()
        .addScaledVector(dir, 1.2)
        .addScaledVector(side, hand === 'left' ? -0.35 : 0.35)
        .add(new THREE.Vector3(0, -0.22, 0));
      end = origin.addScaledVector(dir, WEAPON_MAX_RANGE);
    }

    this.tracers.fire(muzzle, end);
    this.particles.emitMuzzleFlash(muzzle, dir);
    this.engine.audio.shoot();
  }

  private frameUpdate(dt: number): void {
    const cam = this.engine.renderer.camera;

    if (!this.isRunning && !this.xrSession.isInVR) {
      // The title composition calls for a low, exaggerated hero silhouette;
      // gameplay always restores the physically sized vehicle below.
      this.vehicle.mesh.scale.set(2.75, 1.85, 1.85);
    } else {
      this.vehicle.mesh.scale.setScalar(1);
    }
    this.vehicle.setCockpitVisible(this.isRunning || this.xrSession.isInVR);

    if (this.isRunning) {
      const vs = this.vehicleInterp.getInterpolatedState();
      if (vs) {
        let visualTrackT = vs.t;
        let visualSpeed = vs.speed;
        if (this.xrSession.isInVR) {
          if (this.predictedTrackT === null) this.predictedTrackT = this.latestServerTrackT || vs.t;
          // Advance at the headset's render cadence (72/90 Hz), not at the
          // arrival cadence of server patches or the 60 Hz simulation tick.
          this.predictedTrackT = THREE.MathUtils.clamp(
            this.predictedTrackT + (this.predictedSpeed * dt) / Math.max(1, this.railPath.length),
            0,
            1,
          );
          visualTrackT = this.predictedTrackT;
          visualSpeed = this.predictedSpeed;

          // The gunner cannot locally predict the driver's hand, but smoothing
          // toward the newest authority is still substantially more immediate
          // than viewing the 120 ms interpolation history.
          if (this.role !== PlayerRole.Driver && this.predictedLateral !== null) {
            const alpha = 1 - Math.exp(-14 * dt);
            this.predictedLateral += (this.latestServerLateral - this.predictedLateral) * alpha;
          }
        }
        // Only the driver's hands are on the wheel; for the gunner the wheel
        // mirrors how fast the driver is actually moving the car sideways.
        const visualLateral = this.xrSession.isInVR
          ? (this.predictedLateral ?? vs.lateral)
          : vs.lateral;
        const lateralVel = (visualLateral - this.prevLateral) / Math.max(dt, 1e-3);
        this.prevLateral = visualLateral;
        const steering = this.role === PlayerRole.Driver
          ? this.engine.input.currentSteering
          : THREE.MathUtils.clamp(lateralVel / LATERAL_SPEED, -1, 1);
        this.vehicle.setPose(this.railPath, visualTrackT, visualLateral, steering);
        this.vehicle.setSteeringInput(steering);
        this.vehicle.mesh.getWorldPosition(this.lastVehiclePos);

        const speed01 = THREE.MathUtils.clamp((visualSpeed - 10) / (BOOST_SPEED - 10), 0, 1);
        this.engine.audio.setEngine(speed01);

        // subtle speed FOV kick (desktop only — XR owns its projection)
        if (!this.xrSession.isInVR) {
          const targetFov = 72 + (vs.speed > BASE_SPEED + 2 ? 8 : 0);
          cam.fov += (targetFov - cam.fov) * 2.5 * dt;
          cam.updateProjectionMatrix();
        }
      }

      if (!this.xrSession.isInVR) {
        this.vehicle.cameraMount.getWorldPosition(cam.position);
        this.vehicle.cameraMount.getWorldQuaternion(cam.quaternion);
        if (this.shakeAmp > 0.001) {
          cam.position.x += (Math.random() - 0.5) * this.shakeAmp;
          cam.position.y += (Math.random() - 0.5) * this.shakeAmp;
        }
      }
      this.shakeAmp *= Math.exp(-6 * dt);

      this.updateEnemyPoses();

      // HUD
      this.hud.updateHealth(this.serverHealth, VEHICLE_MAX_HEALTH);
      this.hud.updateBoost(this.boostCharge, MAX_BOOST_CHARGE);
      this.hud.updateScore(this.teamScore);
      this.hud.updateCombo(this.myCombo);
      this.hud.updateProgress(this.trackT);
      this.hud.updateMission({
        beatTitle: this.currentBeatTitle,
        objective: this.currentObjective,
        timeRemaining: this.stageTimeRemaining,
        targetTitle: this.targetTitle,
        targetConfiguration: this.targetConfiguration,
        targetState: this.targetState,
        takedowns: this.takedownCount,
      });
      this.vrHud.setVisible(this.xrSession.isInVR);
      this.vrHud.update({
        health: this.serverHealth,
        maxHealth: VEHICLE_MAX_HEALTH,
        boostCharge: this.boostCharge,
        progress: this.trackT,
        boss: this.bossAlive ? this.bossVitals : null,
      });
    } else if (!this.xrSession.isInVR) {
      this.menuTime += dt;
      const focus = this.vehicle.mesh.position;
      const a = this.menuTime * 0.25;
      if (this.trackT < 0.05) {
        // parked in the garage: shallow sweep that stays inside the walls
        const offset = new THREE.Vector3(
          Math.sin(a) * 2.1,
          2.7 + Math.sin(a * 0.6) * 0.12,
          8.15 + Math.cos(a) * 0.32
        ).applyQuaternion(this.vehicle.mesh.quaternion);
        cam.position.copy(focus).add(offset);
        const lookOffset = new THREE.Vector3(0, 1.15, -0.45)
          .applyQuaternion(this.vehicle.mesh.quaternion);
        cam.lookAt(focus.clone().add(lookOffset));
      } else {
        // end screen mid-track: wide open orbit
        cam.position.set(focus.x + Math.sin(a) * 9, 3.2, focus.z + Math.cos(a) * 9);
        cam.lookAt(focus.x, 1, focus.z);
      }
    }

    // in-headset menu: shown whenever you're in VR and not mid-run
    this.vrMenu.setMode(
      !this.xrSession.isInVR ? 'hidden'
      : this.vrBriefingActive ? 'briefing'
      : this.isRunning || this.inCountdown ? 'hidden'
      : this.endInfo ? 'debrief'
      : this.network.sessionId ? (this.isSolo ? 'solo' : 'lobby')
      : 'main'
    );
    this.vrMenu.update();

    this.enemies.update(dt, cam.position);
    this.hazards.update(dt);
    this.engine.scene.updateAtmosphere(dt, this.vehicle.mesh.position);
    this.particles.update(dt);
    this.tracers.update(dt);
    this.damageNumbers.update(dt);
    this.engine.scene.updateSunFocus(this.vehicle.mesh.position);
  }

  private updateEnemyPoses(): void {
    for (const [id, interp] of this.enemyInterps) {
      const es = interp.getInterpolatedState();
      const meta = this.enemyMeta.get(id);
      if (!es || !meta || !this.enemies.has(id)) continue;

      const cfg = ENEMY_CONFIGS[meta.type];
      const height = AIR_TYPES.has(meta.type) ? cfg.hoverHeight + es.altitude : 0;
      this.enemies.setPose(
        id,
        this.railPath.getPosition(es.t, es.lateral, height),
        this.railPath.getQuaternion(es.t)
      );
      this.enemies.setHealth(id, meta.health, meta.maxHealth);
    }
  }

  private projectToScreen(worldPos: THREE.Vector3): { x: number; y: number } | null {
    const cam = this.engine.renderer.camera;
    const projected = worldPos.clone().project(cam);
    if (projected.z > 1) return null; // behind the camera
    return {
      x: (projected.x * 0.5 + 0.5) * window.innerWidth,
      y: (-projected.y * 0.5 + 0.5) * window.innerHeight,
    };
  }
}

/** 5 chars, no ambiguous glyphs (0/O, 1/I). */
function generateLobbyCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 5; i++) {
    code += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return code;
}

// Boot
new Game();
